import {
	H2,
	HStack,
	VStack,
	Text,
	Search,
} from '@montel/montelpro-shared-components-v2';
import { ColumnFilters } from './ColumnFilters/ColumnFilters';
import { useTableContext } from './ColumnFilters/TableContextProvider';
import { debounce } from 'utils/debounce';
import { useEffect, useState } from 'react';
import { useTableViewStateContext } from './TableViewStateContextProvider';

interface TableHeadAreaProps {
	header?: string;
	headerIcon?: React.ReactNode;
	withSearch?: boolean;
	showRowCount?: boolean;
}

export function TableHeadArea<T>(props: TableHeadAreaProps) {
	const { header, headerIcon, withSearch, showRowCount } = props;
	const { table, filterState, tableId } = useTableContext<T>();
	const [value, setValue] = useState('');
	const [previousValue, setPreviousValue] = useState('');
	const hasFilters = filterState && filterState.groups.length > 0;
	const { getViewState, updateViewState } = useTableViewStateContext<T>();

	useEffect(() => {
		const viewState = getViewState(tableId);
		if (viewState?.search) {
			search(viewState.search);
		}
	}, []);

	const search = (searchPhrase: string) => {
		setValue(searchPhrase);
		updateViewState(tableId, { search: searchPhrase });

		const shouldFilter =
			searchPhrase.length > 1 ||
			(previousValue.length > 1 && searchPhrase === '');

		if (shouldFilter) {
			debounce(table.setGlobalFilter(searchPhrase || ''), 500);
			setPreviousValue(searchPhrase);
		}
	};

	return (
		<VStack gap="xl" fullWidth>
			{header && (
				<HStack align="center" gap="md">
					{headerIcon}
					<H2>{header}</H2>
				</HStack>
			)}
			{(hasFilters || withSearch || showRowCount) && (
				<HStack fullWidth align="flex-end" justify={'space-between'}>
					<HStack gap="xl" align="center">
						{withSearch && (
							<Search
								size="sm"
								placeholder="Search"
								value={value}
								onChange={(e) => search(e?.target?.value || '')}
							/>
						)}
						{hasFilters && <ColumnFilters />}
					</HStack>
					{showRowCount && (
						<Text color="textSubtle" size="sm">
							{`${table.getFilteredRowModel().rows.length} ${
								table.getFilteredRowModel().rows.length !== 1
									? 'results'
									: 'result'
							}`}
						</Text>
					)}
				</HStack>
			)}
		</VStack>
	);
}
